import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

/**
 * Hook to check user permissions.
 * Fetches permissions from backend and provides helper functions.
 */
export function usePermissions() {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPermissions();
    } else {
      setPermissions([]);
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const fetchPermissions = async () => {
    try {
      const response = await api.get('/admin/permissions');
      setPermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if user has a specific permission.
   * @param {string} permission - Permission name to check
   * @returns {boolean} True if user has permission
   */
  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  /**
   * Check if user has ANY of the specified permissions.
   * @param {string[]} permissionList - Array of permission names
   * @returns {boolean} True if user has at least one permission
   */
  const hasAnyPermission = (permissionList) => {
    return permissionList.some((perm) => permissions.includes(perm));
  };

  /**
   * Check if user has ALL of the specified permissions.
   * @param {string[]} permissionList - Array of permission names
   * @returns {boolean} True if user has all permissions
   */
  const hasAllPermissions = (permissionList) => {
    return permissionList.every((perm) => permissions.includes(perm));
  };

  /**
   * Check if user is admin (has admin role).
   * @returns {boolean} True if user is admin
   */
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  return {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    refetch: fetchPermissions,
  };
}

/**
 * Component wrapper that only renders children if user has permission.
 * @param {Object} props
 * @param {string} props.permission - Required permission
 * @param {React.ReactNode} props.children - Content to render if permitted
 * @param {React.ReactNode} props.fallback - Content to render if not permitted
 */
export function PermissionGate({ permission, children, fallback = null }) {
  const { hasPermission, loading } = usePermissions();

  if (loading) {
    return null;
  }

  return hasPermission(permission) ? children : fallback;
}

/**
 * Component wrapper that renders children if user has ANY of the permissions.
 * @param {Object} props
 * @param {string[]} props.permissions - Array of permission names
 * @param {React.ReactNode} props.children - Content to render if permitted
 * @param {React.ReactNode} props.fallback - Content to render if not permitted
 */
export function AnyPermissionGate({ permissions, children, fallback = null }) {
  const { hasAnyPermission, loading } = usePermissions();

  if (loading) {
    return null;
  }

  return hasAnyPermission(permissions) ? children : fallback;
}

/**
 * Component wrapper that renders children if user has ALL of the permissions.
 * @param {Object} props
 * @param {string[]} props.permissions - Array of permission names
 * @param {React.ReactNode} props.children - Content to render if permitted
 * @param {React.ReactNode} props.fallback - Content to render if not permitted
 */
export function AllPermissionsGate({ permissions, children, fallback = null }) {
  const { hasAllPermissions, loading } = usePermissions();

  if (loading) {
    return null;
  }

  return hasAllPermissions(permissions) ? children : fallback;
}
